import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Switch,
  IconButton,
} from '@mui/material';
import { PulseLoader } from 'react-spinners';
import { DataGrid, gridClasses, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Iconify } from 'src/components/iconify';
import { toast } from 'src/components/snackbar';
import { executeUserActions, getAdminPageForm } from 'src/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import { decodeJWT } from 'aws-amplify/auth';
import AuthUtils from 'src/utils/AuthUtils';
import { useAuthContext } from 'src/context/AuthContext';
import { useModulePermissions } from 'src/hooks/use-module-permissions';

interface Requirement {
  id: number;
  Label: string;
  Description: string;
  RequiredLevel: number;
  AcceptFileTypes: string | string[];
  RedirectLink: string | null;
  RedirectLinkText: string | null;
  ObjectTypeCode: number;
  IsHidden?: number;
  /** Language-specific fields (when API returns them); fallback to Label/Description/etc. */
  LabelEN?: string;
  LabelES?: string;
  DescriptionEN?: string;
  DescriptionES?: string;
  RedirectLinkEN?: string | null;
  RedirectLinkES?: string | null;
  RedirectLinkTextEN?: string | null;
  RedirectLinkTextES?: string | null;
}

interface LevelOption {
  Code: number;
  DescriptionEN: string;
}

interface RequirementsConfig {
  accepted_file_types: string[];
  requirement_types: Array<{ code: number; label: string }>;
  sql_data: {
    fetch_requirements: { sql: string; variables: any[] };
    fetch_levels: { sql: string; variables: any[] };
    check_label_exists: { sql: string; variables: any[] };
    check_label_exists_edit: { sql: string; variables: any[] };
    insert_requirement: { sql: string; variables: any[] };
    update_requirement: { sql: string; variables: any[] };
    delete_requirement: { sql: string; variables: any[] };
    toggle_requirement: { sql: string; variables: any[] };
  };
  columns: any[];
}

export function RequirementsManagementView() {
  const modulePermissions = useModulePermissions('control-panel');
  const [config, setConfig] = useState<RequirementsConfig | null>(null);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [levels, setLevels] = useState<LevelOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState<Requirement | null>(null);
  const [requirementLang, setRequirementLang] = useState<'en' | 'es'>('en');
  const [formData, setFormData] = useState({
    LabelEN: '',
    LabelES: '',
    DescriptionEN: '',
    DescriptionES: '',
    RequiredLevel: '',
    ObjectTypeCode: '',
    AcceptFileTypes: [] as string[],
    RedirectLinkEN: '',
    RedirectLinkES: '',
    RedirectLinkTextEN: '',
    RedirectLinkTextES: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [levelError, setLevelError] = useState<string | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);
  const [acceptFileTypesError, setAcceptFileTypesError] = useState<string | null>(null);
  const [checkingLabel, setCheckingLabel] = useState(false);
  const [acceptFileTypesOpen, setAcceptFileTypesOpen] = useState(false);

  const { authState } = useAuthContext();
  const { isAuthenticated } = authState;

  // Fetch configuration from JSON
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const res = await getAdminPageForm('control-panel');
        if (res?.pageForm?.sub_sections) {
          const requirementsSection = res.pageForm.sub_sections.find(
            (section: any) => section.name === 'requirements-management'
          );
          if (requirementsSection?.requirements_config) {
            setConfig(requirementsSection.requirements_config);
            setLoading(false);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Get user ID from token
  const getUserId = useCallback(async () => {
    try {
      const token = isAuthenticated ? AuthUtils.getAuthToken() : null;
      if (token) {
        const decoded = await decodeJWT(token);
        const user = (decoded?.payload as any)?.user || (decoded?.payload as any);
        return user?.UserId || user?.user_id || null;
      }
    } catch (error) {
      // Error handled silently
    }
    return null;
  }, [isAuthenticated]);

  // Fetch requirements list
  const fetchRequirements = useCallback(async () => {
    if (!config) return;
    
    try {
      setLoading(true);
      const sqlData = config.sql_data.fetch_requirements;

      const response = await executeUserActions('control-panel', {
        tk: EncryptUtils.encryptedData({ sql_data: sqlData } as any),
      });

      if (response?.success && response?.data) {
        const data = Array.isArray(response.data) ? response.data.flat() : [];
        const formatted = data
          .filter((item: any) => {
            // Filter out invalid rows - must have at least id or Code, and must be an object with properties
            if (!item || typeof item !== 'object') return false;
            if (Object.keys(item).length === 0) return false;
            return !!(item.id || item.Code || item.PresentationRequirementTypeId);
          })
          .map((item: any) => {
            // Ensure id exists and is a valid number
            const id = item.id || item.Code || item.PresentationRequirementTypeId;
            if (!id && id !== 0) {
              // Skip rows without valid id
              return null;
            }
            // Parse AcceptFileTypes - handle "*" as default
            let acceptFileTypes = [];
            if (typeof item.AcceptFileTypes === 'string') {
              try {
                acceptFileTypes = JSON.parse(item.AcceptFileTypes || '[]');
              } catch {
                // If not JSON, treat as comma-separated string
                acceptFileTypes = item.AcceptFileTypes.split(',').map((t: string) => t.trim()).filter((t: string) => t);
              }
            } else {
              acceptFileTypes = item.AcceptFileTypes || [];
            }
            
            const labelEn = item.LabelEN ?? item.Label ?? '';
            const labelEs = item.LabelES ?? '';
            const descEn = item.DescriptionEN ?? item.Description ?? '';
            const descEs = item.DescriptionES ?? '';
            const linkEn = item.RedirectLinkEN ?? item.RedirectLink ?? '';
            const linkEs = item.RedirectLinkES ?? '';
            const linkTextEn = item.RedirectLinkTextEN ?? item.RedirectLinkText ?? '';
            const linkTextEs = item.RedirectLinkTextES ?? '';
            return {
              ...item,
              id: Number(id),
              Label: labelEn,
              Description: descEn,
              RedirectLink: linkEn,
              RedirectLinkText: linkTextEn,
              LabelEN: labelEn,
              LabelES: labelEs,
              DescriptionEN: descEn,
              DescriptionES: descEs,
              RedirectLinkEN: linkEn,
              RedirectLinkES: linkEs,
              RedirectLinkTextEN: linkTextEn,
              RedirectLinkTextES: linkTextEs,
              AcceptFileTypes: acceptFileTypes,
              ObjectTypeCode: item.ObjectTypeCode || 100038,
              IsHidden: item.IsHidden !== undefined && item.IsHidden !== null ? Number(item.IsHidden) : 0,
            };
          })
          .filter((item: any) => item !== null); // Remove null entries
        setRequirements(formatted);
      }
    } catch (error) {
      toast.error('Failed to load requirements');
    } finally {
      setLoading(false);
    }
  }, [config]);

  // Fetch level options
  const fetchLevels = useCallback(async () => {
    if (!config) return;
    
    try {
      const sqlData = config.sql_data.fetch_levels;

      const response = await executeUserActions('control-panel', {
        tk: EncryptUtils.encryptedData({ sql_data: sqlData } as any),
      });

      if (response?.success && response?.data) {
        const data = Array.isArray(response.data) ? response.data.flat() : [];
        if (data.length > 0 && data[0]?.Code && data[0]?.DescriptionEN) {
          setLevels(data);
          return;
        }
      }

      // Fallback: Use common level values from the image (Pic 2)
      // Code: 1001, 1002, 1003, 100001
      // DescriptionEN: Required if applicable, Required, Dynamic, Not Required
      setLevels([
        { Code: 1001, DescriptionEN: 'Required if applicable' },
        { Code: 1002, DescriptionEN: 'Required' },
        { Code: 1003, DescriptionEN: 'Dynamic' },
        { Code: 100001, DescriptionEN: 'Not Required' },
      ]);
    } catch (error) {
      // If query fails, use default level options
      setLevels([
        { Code: 1001, DescriptionEN: 'Required if applicable' },
        { Code: 1002, DescriptionEN: 'Required' },
        { Code: 1003, DescriptionEN: 'Dynamic' },
        { Code: 100001, DescriptionEN: 'Not Required' },
      ]);
    }
  }, [config]);

  useEffect(() => {
    if (config) {
      fetchRequirements();
      fetchLevels();
    }
  }, [config, fetchRequirements, fetchLevels]);

  // Open add dialog
  const handleAdd = () => {
    setSelectedRequirement(null);
    setFormData({
      LabelEN: '',
      LabelES: '',
      DescriptionEN: '',
      DescriptionES: '',
      RequiredLevel: '',
      ObjectTypeCode: '',
      AcceptFileTypes: [],
      RedirectLinkEN: '',
      RedirectLinkES: '',
      RedirectLinkTextEN: '',
      RedirectLinkTextES: '',
    });
    setValidationError(null);
    setLabelError(null);
    setDescriptionError(null);
    setLevelError(null);
    setTypeError(null);
    setAcceptFileTypesError(null);
    setDialogOpen(true);
  };

  // Open edit dialog
  const handleEdit = (requirement: Requirement) => {
    // Permission check: Edit button is only shown if user has permission (checked at render)
    // This is a safety check in case permissions change or edge cases
    if (!modulePermissions.canViewDetails) {
      return;
    }
    
    setSelectedRequirement(requirement);
    const acceptFileTypes = Array.isArray(requirement.AcceptFileTypes)
      ? requirement.AcceptFileTypes 
      : [];
    setFormData({
      LabelEN: requirement.LabelEN ?? requirement.Label ?? '',
      LabelES: requirement.LabelES ?? '',
      DescriptionEN: requirement.DescriptionEN ?? requirement.Description ?? '',
      DescriptionES: requirement.DescriptionES ?? '',
      RequiredLevel: String(requirement.RequiredLevel || ''),
      ObjectTypeCode: String(requirement.ObjectTypeCode || 100038),
      AcceptFileTypes: acceptFileTypes,
      RedirectLinkEN: requirement.RedirectLinkEN ?? requirement.RedirectLink ?? '',
      RedirectLinkES: requirement.RedirectLinkES ?? '',
      RedirectLinkTextEN: requirement.RedirectLinkTextEN ?? requirement.RedirectLinkText ?? '',
      RedirectLinkTextES: requirement.RedirectLinkTextES ?? '',
    });
    setValidationError(null);
    setLabelError(null);
    setDescriptionError(null);
    setLevelError(null);
    setTypeError(null);
    setAcceptFileTypesError(null);
    setDialogOpen(true);
  };

  // Handle toggle (show/hide requirement) - uses stored procedure
  const handleToggle = useCallback(async (requirement: Requirement) => {
    try {
      const userId = await getUserId();
      const newIsHidden = requirement.IsHidden === 1 ? 0 : 1;
      
      if (!config) {
        toast.error('Configuration not loaded. Please refresh the page.');
        return;
      }

      // Use stored procedure for toggle from config
      const sqlData = config.sql_data.toggle_requirement;

      const response = await executeUserActions('control-panel', {
        tk: EncryptUtils.encryptedData({
          sql_data: sqlData,
          requirement_type_id: requirement.id,
          is_hidden: newIsHidden,
          user_id: userId,
        } as any),
      });

      if (response?.success) {
        toast.success(newIsHidden === 1 ? 'Requirement hidden successfully' : 'Requirement shown successfully');
        fetchRequirements();
      } else {
        toast.error('Failed to update requirement');
      }
    } catch (error) {
      toast.error('Failed to update requirement');
    }
  }, [getUserId, fetchRequirements, config]);

  // Open delete dialog
  const handleDeleteClick = (requirement: Requirement) => {
    // Permission check: Delete button is only shown if user has permission (checked at render)
    // This is a safety check in case permissions change or edge cases
    if (!modulePermissions.canDelete) {
      return;
    }
    
    setSelectedRequirement(requirement);
    setValidationError(null);
    setDeleteDialogOpen(true);
  };


  // Handle delete - Hard delete (permanent) using stored procedure
  const handleDelete = useCallback(async () => {
    if (!selectedRequirement) return;

    // Permission check: Delete button is only shown if user has permission (checked at render)
    // This is a safety check in case permissions change or edge cases
    if (!modulePermissions.canDelete) {
      setDeleteDialogOpen(false);
      return;
    }

    try {
      setDeleting(true);
      setValidationError(null);

      if (!config) {
        toast.error('Configuration not loaded. Please refresh the page.');
        setDeleting(false);
        return;
      }

      // Hard delete - use stored procedure from config
      const sqlData = config.sql_data.delete_requirement;

      const response = await executeUserActions('control-panel', {
        tk: EncryptUtils.encryptedData({
          sql_data: sqlData,
          requirement_type_id: selectedRequirement.id,
        } as any),
      });

      if (response?.success) {
        toast.success('Requirement deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedRequirement(null);
        fetchRequirements();
      } else {
        toast.error('Failed to delete requirement');
      }
    } catch (error: any) {
      toast.error('Failed to delete requirement');
    } finally {
      setDeleting(false);
    }
  }, [selectedRequirement, fetchRequirements, config, modulePermissions.canDelete]);

  // Check if Label already exists in database
  const checkLabelExists = useCallback(async (label: string, excludeCode?: number): Promise<boolean> => {
    if (!config) return false;
    
    try {
      // For SELECT queries with direct value replacement (like the original submit logic)
      const escapedLabel = label.trim().replace(/'/g, "''");
      let sql = excludeCode !== undefined
        ? config.sql_data.check_label_exists_edit.sql
        : config.sql_data.check_label_exists.sql;
      
      // Replace placeholders with actual values
      sql = sql.replace('###Label', escapedLabel);
      if (excludeCode !== undefined) {
        sql = sql.replace('###Code', String(excludeCode));
      }

      const sqlData = {
        sql,
        variables: [],
      };

      const payload: any = {
        sql_data: sqlData,
      };

      const response = await executeUserActions('control-panel', {
        tk: EncryptUtils.encryptedData(payload as any),
      });
      
      if (response?.success && response?.data) {
        const data = Array.isArray(response.data) ? response.data.flat() : response.data;
        
        // Handle different response structures
        let count = 0;
        if (Array.isArray(data) && data.length > 0) {
          count = data[0]?.LabelCount || data[0]?.labelCount || 0;
        } else if (data && typeof data === 'object') {
          count = data.LabelCount || data.labelCount || 0;
        }
        
        return count > 0;
      }
      return false;
    } catch (error) {
      return false; // If check fails, allow submission (fail-safe)
    }
  }, [config]);

  // Handle form submit (Add/Edit)
  const handleSubmit = useCallback(async () => {
    // Permission check: Edit button is only shown if user has permission (checked at render)
    // This is a safety check in case permissions change or edge cases
    // Only check when editing, not when adding
    if (selectedRequirement) {
      if (!modulePermissions.canEditDetails) {
        return;
      }
    }
    
    // Clear all field errors first
    setDescriptionError(null);
    setLevelError(null);
    setTypeError(null);
    setAcceptFileTypesError(null);
    setValidationError(null);

    const currentLabel = requirementLang === 'en' ? formData.LabelEN : formData.LabelES;
    const currentDescription = requirementLang === 'en' ? formData.DescriptionEN : formData.DescriptionES;

    // Unique label: LabelEN is the canonical key. LabelES can be same as LabelEN or empty (then we use LabelEN for both).
    const canonicalLabel = (formData.LabelEN || '').trim();

    // Validate each field individually - show all errors at once
    let hasErrors = false;
    
    // Validate Label: LabelEN is required (unique identifier). LabelES optional; when empty we use LabelEN for both.
    if (!canonicalLabel) {
      setLabelError('Label (English) is required. Use the same value for Spanish if you want one unique label with EN/ES data.');
      hasErrors = true;
    } else if (labelError) {
      hasErrors = true;
    }
    
    // Validate Description (current language)
    if (!currentDescription || !currentDescription.trim()) {
      setDescriptionError('Description is required.');
      hasErrors = true;
    }
    
    // Validate Level
    if (!formData.RequiredLevel) {
      setLevelError('Level is required.');
      hasErrors = true;
    }
    
    // Validate Type
    if (!formData.ObjectTypeCode) {
      setTypeError('Type is required.');
      hasErrors = true;
    }
    
    // Validate Accept File Types
    if (!formData.AcceptFileTypes || formData.AcceptFileTypes.length === 0) {
      setAcceptFileTypesError('Accept File Types is required. Please select at least one file type.');
      hasErrors = true;
    }

    // If there are any errors, stop here and show all errors
    if (hasErrors) {
      return;
    }
    
    // Clear label error if validation passed
    setLabelError(null);

    try {
      setSubmitting(true);
      setValidationError(null);

      // Check if Label already exists: use canonical label (LabelEN) as the unique key
      const isNewRequirement = !selectedRequirement;
      const existingLabel = (selectedRequirement?.LabelEN ?? selectedRequirement?.Label ?? '').trim();
      const labelChanged = selectedRequirement && existingLabel !== canonicalLabel;
      
      // Only check label existence if we need to (new requirement or label changed)
      // Don't re-check if labelError already exists from blur validation
      if (isNewRequirement || labelChanged) {
        const excludeCode = selectedRequirement?.id;
        const labelExists = await checkLabelExists(canonicalLabel, excludeCode);
        if (labelExists) {
          setLabelError('A requirement with this Label already exists. Please choose a different label.');
          setSubmitting(false);
          return;
        }
      }

      const userId = await getUserId();

      if (!config) {
        toast.error('Configuration not loaded. Please refresh the page.');
        setSubmitting(false);
        return;
      }

      // Get SQL data from config
      const sqlData = selectedRequirement 
        ? config.sql_data.update_requirement
        : config.sql_data.insert_requirement;

      // Handle Accept File Types - remove "*" if user selected specific types
      let finalAcceptFileTypes = [...formData.AcceptFileTypes];
      if (finalAcceptFileTypes.includes('*') && finalAcceptFileTypes.length > 1) {
        // Remove "*" if user selected other types
        finalAcceptFileTypes = finalAcceptFileTypes.filter(t => t !== '*');
      }
      const acceptFileTypesJson = JSON.stringify(finalAcceptFileTypes);

      // When LabelES is empty or same as LabelEN, use LabelEN for both so one unique label has EN/ES data
      const labelEs = (formData.LabelES || '').trim() || canonicalLabel;
      const payload: any = {
        sql_data: sqlData,
        label: canonicalLabel,
        description: formData.DescriptionEN,
        required_level: parseInt(formData.RequiredLevel, 10),
        object_type_code: parseInt(formData.ObjectTypeCode, 10),
        accept_file_types: acceptFileTypesJson,
        user_id: userId,
        redirect_link: formData.RedirectLinkEN?.trim() || null,
        redirect_link_text: formData.RedirectLinkTextEN?.trim() || null,
        label_en: canonicalLabel,
        label_es: labelEs,
        description_en: formData.DescriptionEN,
        description_es: formData.DescriptionES || null,
        redirect_link_en: formData.RedirectLinkEN?.trim() || null,
        redirect_link_es: formData.RedirectLinkES?.trim() || null,
        redirect_link_text_en: formData.RedirectLinkTextEN?.trim() || null,
        redirect_link_text_es: formData.RedirectLinkTextES?.trim() || null,
      };

      if (selectedRequirement) {
        payload.requirement_type_id = selectedRequirement.id;
      }

      const response = await executeUserActions('control-panel', {
        tk: EncryptUtils.encryptedData(payload as any),
      });

      if (response?.success) {
        toast.success(selectedRequirement ? 'Requirement updated successfully' : 'Requirement added successfully');
        setDialogOpen(false);
        setSelectedRequirement(null);
        fetchRequirements();
      } else {
        // Check if error message indicates duplicate label
        const errorMessage = response?.message || response?.error || '';
        if (errorMessage.includes('Label already exists') || errorMessage.includes('already exists')) {
          setLabelError('A requirement with this Label already exists. Please choose a different label.');
        } else {
          toast.error(selectedRequirement ? 'Failed to update requirement' : 'Failed to add requirement');
        }
      }
    } catch (error: any) {
      // Handle SQL error from stored procedure
      const errorMessage = error?.message || error?.response?.data?.message || '';
      if (errorMessage.includes('Label already exists') || errorMessage.includes('already exists')) {
        setLabelError('A requirement with this Label already exists. Please choose a different label.');
      } else {
        toast.error(selectedRequirement ? 'Failed to update requirement' : 'Failed to add requirement');
      }
    } finally {
      setSubmitting(false);
    }
  }, [formData, selectedRequirement, requirementLang, getUserId, fetchRequirements, checkLabelExists, config, labelError, modulePermissions.canEditDetails]);

  const columns: GridColDef[] = [
    {
      field: 'Label',
      headerName: 'Label',
      width: 200,
      sortable: true,
      valueGetter: (params: any) => {
        const row = params.row;
        return (requirementLang === 'es' ? row.LabelES : row.LabelEN) ?? row.Label ?? '';
      },
    },
    {
      field: 'Description',
      headerName: 'Description',
      flex: 2,
      sortable: true,
      valueGetter: (params: any) => {
        const row = params.row;
        return (requirementLang === 'es' ? row.DescriptionES : row.DescriptionEN) ?? row.Description ?? '';
      },
    },
    {
      field: 'ObjectTypeCode',
      headerName: 'Type',
      width: 150,
      sortable: true,
      valueGetter: (params: any) => {
        const row = params.row;
        if (!config) return String(row.ObjectTypeCode);
        const type = config.requirement_types.find((t) => t.code === row.ObjectTypeCode);
        return type?.label || String(row.ObjectTypeCode);
      },
    },
    {
      field: 'RequiredLevel',
      headerName: 'Level',
      width: 150,
      sortable: true,
      valueGetter: (params: any) => {
        const row = params.row;
        const level = levels.find((l) => l.Code === row.RequiredLevel);
        return level?.DescriptionEN || String(row.RequiredLevel);
      },
    },
    {
      field: 'AcceptFileTypes',
      headerName: 'Accept File Types',
      width: 200,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const types = Array.isArray(params.value) ? params.value : [];
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
              {types.length > 0 ? types.join(', ') : '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'RedirectLink',
      headerName: 'Redirect Link',
      flex: 1,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params: any) => {
        const row = params.row;
        return (requirementLang === 'es' ? row.RedirectLinkES : row.RedirectLinkEN) ?? row.RedirectLink ?? '';
      },
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          {params.value ? (
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
              {params.value}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              -
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'RedirectLinkText',
      headerName: 'Redirect Link Text',
      width: 180,
      sortable: false,
      valueGetter: (params: any) => {
        const row = params.row;
        return (requirementLang === 'es' ? row.RedirectLinkTextES : row.RedirectLinkTextEN) ?? row.RedirectLinkText ?? '';
      },
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 200,
      getActions: (params) => {
        // Only show actions if user has CONTROL_PANEL:ACTIONS permission (controls visibility)
        if (!modulePermissions.canViewActions) {
          return [];
        }
        
        // Use module permissions for all checks
        const canPerformOperations = modulePermissions.canEdit;
        const canViewDetails = modulePermissions.canViewDetails;
        const canDelete = modulePermissions.canDelete;
        
        // Build actions array conditionally
        const actions = [
          // Toggle: Always show if CONTROL_PANEL:ACTIONS is present (already checked above)
          <GridActionsCellItem
            key="toggle"
            icon={
              <Switch
                checked={params.row.IsHidden !== 1}
                size="small"
                onClick={(e) => e.stopPropagation()}
                onChange={() => handleToggle(params.row)}
                disabled={!canPerformOperations}
              />
            }
            label={params.row.IsHidden === 1 ? 'Show' : 'Hide'}
            showInMenu={false}
          />,
        ];
        
        // Edit icon: Only show if user has CONTROL_PANEL:DETAILS_VIEW permission
        if (canViewDetails) {
          actions.push(
            <GridActionsCellItem
              key="edit"
              icon={<Iconify icon="eva:external-link-fill" />}
              label="Edit"
              onClick={() => handleEdit(params.row)}
              showInMenu={false}
            />
          );
        }
        
        // Delete icon: Only show if user has CONTROL_PANEL:DELETE permission
        if (canDelete) {
          actions.push(
            <GridActionsCellItem
              key="delete"
              icon={<Iconify icon="solar:trash-bin-trash-bold" />}
              label="Delete"
              onClick={() => handleDeleteClick(params.row)}
              showInMenu={false}
            />
          );
        }
        
        return actions;
      },
    },
  ];

  // Show loading state if config is not loaded yet - use PulseLoader to match other tabs
  if (!config) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, width: '100%' }}>
        <PulseLoader size={12} />
      </Box>
    );
  }

  return (
    <Box
      p={2}
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={{ xs: 2, sm: 0 }}
        mb={3}
      >
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          <Typography 
            component="h2"
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
              fontWeight: 700,
            }}
          >
            Requirements Management
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="requirement-lang-label">Language</InputLabel>
            <Select
              labelId="requirement-lang-label"
              value={requirementLang}
              label="Language"
              onChange={(e) => setRequirementLang(e.target.value as 'en' | 'es')}
              MenuProps={{ disableScrollLock: true }}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Español</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Button
          variant="contained"
          size="medium"
          startIcon={<Iconify icon="solar:add-circle-bold" />}
          onClick={handleAdd}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            py: { xs: 0.75, md: 1 },
            px: { xs: 1.5, md: 2 },
          }}
        >
          Add Requirement
        </Button>
      </Stack>

      <DataGrid
        rows={requirements}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.id || row.Code || String(row.PresentationRequirementTypeId || Math.random())}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        sx={(theme) => ({
          flex: '1 1 auto',
          minHeight: 0,
          [`& .${gridClasses.cell}`]: {
            alignItems: 'center',
            display: 'inline-flex',
            borderTop: `1px solid ${theme.palette.divider}`,
          },
          [`& .${gridClasses.columnHeaders}`]: {
            backgroundColor:
              theme.palette.mode === 'light' ? '#F1F3F5' : theme.palette.background.paper,
          },
          [`& .${gridClasses.columnHeader}`]: {
            backgroundColor: 'transparent',
          },
        })}
      />

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => {
          if (!submitting) {
            setDialogOpen(false);
            setLabelError(null);
            setValidationError(null);
          }
        }} 
        maxWidth="md"
        sx={{
          '& .MuiDialog-paper': {
            width: { xs: '90%', sm: '100%' },
            maxWidth: { xs: '500px', md: '900px' },
            m: { xs: 2, md: 3 },
          },
        }}
        fullWidth
        disableEscapeKeyDown={submitting}
        disableRestoreFocus
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <span>{selectedRequirement ? 'View/Edit Requirement' : 'Add Requirement'}</span>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="dialog-lang-label">Language</InputLabel>
              <Select
                labelId="dialog-lang-label"
                value={requirementLang}
                label="Language"
                onChange={(e) => setRequirementLang(e.target.value as 'en' | 'es')}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Español</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
          <Stack spacing={{ xs: 2, md: 3 }} sx={{ mt: 1 }}>
            {(() => {
              const isViewing = !!selectedRequirement;
              const canEditDetails = modulePermissions.canEditDetails;
              const isReadOnly = isViewing && !canEditDetails;
              // Label is one value for both EN and ES for now; typing in either language updates both (LabelES column kept for future separate labels).
              const labelVal = formData.LabelEN || formData.LabelES || '';
              const descriptionVal = requirementLang === 'en' ? formData.DescriptionEN : formData.DescriptionES;
              const redirectLinkVal = requirementLang === 'en' ? formData.RedirectLinkEN : formData.RedirectLinkES;
              const redirectLinkTextVal = requirementLang === 'en' ? formData.RedirectLinkTextEN : formData.RedirectLinkTextES;
              
              return (
                <>
            <TextField
              label="Label"
              value={labelVal}
              disabled={isReadOnly}
              onChange={(e) => {
                if (isReadOnly) return;
                const value = e.target.value;
                // One label for both: typing in EN or ES updates both so save writes same to LabelEN and LabelES.
                setFormData((prev) => ({
                  ...prev,
                  LabelEN: value,
                  LabelES: value,
                }));
                if (labelError) setLabelError(null);
              }}
              onBlur={async () => {
                // Use canonical label (LabelEN preferred) for uniqueness check so same label in EN/ES is one unique value
                const canonical = (formData.LabelEN || formData.LabelES || '').trim();
                if (!canonical) {
                  return; // Don't validate empty labels on blur
                }
                setCheckingLabel(true);
                try {
                  const excludeCode = selectedRequirement?.id;
                  const exists = await checkLabelExists(canonical, excludeCode);
                  if (exists) {
                    setLabelError('A requirement with this Label already exists. Please choose a different label.');
                  } else {
                    setLabelError(null);
                  }
                } catch (error) {
                  // Silently fail on blur - don't show error if API call fails
                } finally {
                  setCheckingLabel(false);
                }
              }}
              fullWidth
              required
              error={!!labelError}
              helperText={labelError || "Unique identifier (no spaces). One value for both EN and ES; saves to both columns. (LabelES column kept for future separate labels.)"}
            />

            <TextField
              label="Description"
              value={descriptionVal}
              disabled={isReadOnly}
              onChange={(e) => {
                if (isReadOnly) return;
                setFormData((prev) => ({
                  ...prev,
                  ...(requirementLang === 'en' ? { DescriptionEN: e.target.value } : { DescriptionES: e.target.value }),
                }));
                if (descriptionError) setDescriptionError(null);
              }}
              fullWidth
              required
              multiline
              rows={3}
              error={!!descriptionError}
              helperText={descriptionError}
            />

            <FormControl fullWidth required error={!!typeError}>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.ObjectTypeCode}
                disabled={isReadOnly}
                onChange={(e) => {
                  if (isReadOnly) return;
                  setFormData({ ...formData, ObjectTypeCode: e.target.value });
                  if (typeError) {
                    setTypeError(null);
                  }
                }}
                label="Type"
              >
                {config.requirement_types.map((type) => (
                  <MenuItem key={type.code} value={String(type.code)}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {typeError && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{typeError}</Typography>}
            </FormControl>

            <FormControl fullWidth required error={!!levelError}>
              <InputLabel>Level</InputLabel>
              <Select
                value={formData.RequiredLevel}
                disabled={isReadOnly}
                onChange={(e) => {
                  if (isReadOnly) return;
                  setFormData({ ...formData, RequiredLevel: e.target.value });
                  if (levelError) {
                    setLevelError(null);
                  }
                }}
                label="Level"
              >
                {levels.map((level) => (
                  <MenuItem key={level.Code} value={String(level.Code)}>
                    {level.DescriptionEN}
                  </MenuItem>
                ))}
              </Select>
              {levelError && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{levelError}</Typography>}
            </FormControl>

            <FormControl fullWidth required error={!!acceptFileTypesError}>
              <InputLabel>Accept File Types</InputLabel>
              <Select
                multiple
                disabled={isReadOnly}
                open={acceptFileTypesOpen && !isReadOnly}
                onOpen={() => !isReadOnly && setAcceptFileTypesOpen(true)}
                onClose={() => setAcceptFileTypesOpen(false)}
                value={formData.AcceptFileTypes}
                onChange={(e) => {
                  if (isReadOnly) return;
                  if (acceptFileTypesError) {
                    setAcceptFileTypesError(null);
                  }
                  const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                  
                  // Get previous value to detect what changed
                  const previousValue = formData.AcceptFileTypes;
                  const hadAsterisk = previousValue.includes('*');
                  const hasAsterisk = selected.includes('*');
                  
                  // If user explicitly clicks "*" (wasn't selected before, now is)
                  if (hasAsterisk && !hadAsterisk) {
                    // Close the dropdown immediately when "*" is selected
                    setAcceptFileTypesOpen(false);
                    // User clicked "*" - clear everything else and set only "*"
                    setFormData((prev) => ({ ...prev, AcceptFileTypes: ['*'] }));
                  } else {
                    // If user clicks a specific file type while "*" is selected
                    // Remove "*" and keep only the specific types
                    const newValue = selected.filter(t => t !== '*');
                    setFormData((prev) => ({ ...prev, AcceptFileTypes: newValue }));
                  }
                }}
                label="Accept File Types"
                renderValue={(selected) => (
                  <Box 
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                    onMouseDown={(e) => {
                      // Prevent Select from opening when clicking delete buttons
                      if ((e.target as HTMLElement).closest('.chip-delete-btn')) {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    }}
                  >
                    {(selected as string[]).map((value) => (
                      <Chip
                        key={value}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 0.5 }}>
                            <span>{value}</span>
                            {!isReadOnly && (
                            <IconButton
                              size="small"
                              className="chip-delete-btn"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setFormData((prev) => {
                                  const currentValue = prev.AcceptFileTypes;
                                  const newValue = currentValue.filter((item) => item !== value);
                                  // If nothing selected after deletion, default to "*"
                                  const finalValue = newValue.length > 0 ? newValue : ['*'];
                                  return {
                                    ...prev,
                                    AcceptFileTypes: finalValue,
                                  };
                                });
                              }}
                              sx={{
                                width: 16,
                                height: 16,
                                padding: 0,
                                ml: 0.5,
                                '&:hover': {
                                  backgroundColor: 'error.lighter',
                                  color: 'error.main',
                                },
                              }}
                            >
                              <Iconify icon="eva:close-fill" width={12} />
                            </IconButton>
                            )}
                          </Box>
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          backgroundColor: 'transparent',
                          borderColor: 'divider',
                          '& .MuiChip-label': {
                            padding: '4px 0px 4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
              >
                <MenuItem 
                  value="*"
                  sx={{ display: formData.AcceptFileTypes.includes('*') ? 'none' : 'flex' }}
                >
                  * (All file types)
                </MenuItem>
                {config.accepted_file_types.map((type) => (
                  <MenuItem
                    key={type}
                    value={type}
                    sx={{ display: formData.AcceptFileTypes.includes(type) ? 'none' : 'flex' }}
                  >
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {acceptFileTypesError && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{acceptFileTypesError}</Typography>}
            </FormControl>

            <TextField
              label="Redirect Link"
              value={redirectLinkVal}
              disabled={isReadOnly}
              onChange={(e) => {
                if (isReadOnly) return;
                setFormData((prev) => ({
                  ...prev,
                  ...(requirementLang === 'en' ? { RedirectLinkEN: e.target.value } : { RedirectLinkES: e.target.value }),
                }));
              }}
              fullWidth
              placeholder="https://example.com/form"
              helperText="Optional: URL to redirect users to a form or resource"
            />

            <TextField
              label="Redirect Link Text"
              value={redirectLinkTextVal}
              disabled={isReadOnly}
              onChange={(e) => {
                if (isReadOnly) return;
                setFormData((prev) => ({
                  ...prev,
                  ...(requirementLang === 'en' ? { RedirectLinkTextEN: e.target.value } : { RedirectLinkTextES: e.target.value }),
                }));
              }}
              fullWidth
              placeholder="Verification of Employment form"
              helperText="Optional: Text to display for the redirect link"
            />
                </>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={submitting}
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          {/* Hide Update button if user doesn't have CONTROL_PANEL:DETAILS_EDIT permission (only when editing, not when adding) */}
          {(!selectedRequirement || modulePermissions.canEditDetails) && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
              fullWidth={false}
              startIcon={submitting ? <CircularProgress size={16} /> : <Iconify icon="solar:check-circle-bold" />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {submitting ? 'Saving...' : selectedRequirement ? 'Update' : 'Add'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={{
          '& .MuiDialog-paper': {
            width: { xs: '90%', sm: '100%' },
            maxWidth: { xs: '400px', md: '600px' },
            m: { xs: 2, md: 3 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>Delete Requirement</DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
          <Typography>
            Are you sure you want to delete this requirement? This action cannot be undone and will permanently delete the requirement from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            fullWidth={false}
            startIcon={deleting ? <CircularProgress size={16} /> : <Iconify icon="solar:trash-bin-trash-bold" />}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

